import { createSlice } from '@reduxjs/toolkit';
import { signIn, forgotPassword, resetPassword, resendOtp, logout, verifyActiveSession } from './authAction';
import { AuthState } from './authTypes'

// Initial state
const initialState: AuthState = {
  user: null,
  token: null,
  forgotUserData: null,
  signInError: null,
  forgotPasswordError: null,
  resetPasswordError: null,
  resendOtpError: null,
  signInLoading: false,
  forgotPasswordLoading: false,
  resetPasswordLoading: false,
  resendOtpLoading: false,
 
};

// Retrieve user and token from localStorage (if they exist)
const userFromStorage = localStorage.getItem('user');
const tokenFromStorage = localStorage.getItem('token');

// Update initial state with values from localStorage
const initialStateFromStorage = userFromStorage && tokenFromStorage ? {
  user: JSON.parse(userFromStorage),
  token: tokenFromStorage,
  signInError: null,
  forgotPasswordError: null,
  resetPasswordError: null,
  resendOtpError: null,
  signInLoading: false,
  forgotPasswordLoading: false,
  resetPasswordLoading: false,
  resendOtpLoading: false,
  verifySessionStatus: 'idle',
  isSessionActive: undefined,
  verifySessionError: null,
} : initialState;

const authSlice = createSlice({
  name: 'auth',
  initialState: initialStateFromStorage,
  reducers: {
    logOut: (state) => {
      state.user = null;
      state.token = null;
      // Remove user and token from localStorage when logging out
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      document.cookie = '__SESSION=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'; // Clears the session cookie
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signIn.pending, (state) => {
        state.signInLoading = true;
        state.signInError = null;
      })
      .addCase(signIn.fulfilled, (state, action) => {
        state.signInLoading = false;
        state.user = action.payload.data;
        state.token = action.payload.data.uniqueSessionId;

        // Save user and token to localStorage
        localStorage.setItem('user', JSON.stringify(action.payload.data));
        localStorage.setItem('token', action.payload.data.uniqueSessionId);
      })
      .addCase(signIn.rejected, (state, action) => {
        state.signInLoading = false;
        state.signInError = action.payload as string;
      })

      // Other cases for forgotPassword, resetPassword, resendOtp, etc.
      .addCase(forgotPassword.pending, (state) => {
        state.forgotPasswordLoading = true;
        state.forgotPasswordError = null;
      })
      .addCase(forgotPassword.fulfilled, (state: any, action: any) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordError = null;
        state.forgotUserData = action.payload.data;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.forgotPasswordLoading = false;
        state.forgotPasswordError = action.payload as string;
      })

      .addCase(resetPassword.pending, (state) => {
        state.resetPasswordLoading = true;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordError = null;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.resetPasswordLoading = false;
        state.resetPasswordError = action.payload as string;
      })

      .addCase(resendOtp.pending, (state) => {
        state.resendOtpLoading = true;
        state.resendOtpError = null;
      })
      .addCase(resendOtp.fulfilled, (state, action) => {
        state.resendOtpLoading = false;
        state.resendOtpError = null;
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.resendOtpLoading = false;
        state.resendOtpError = action.payload as string;
      })
      .addCase(verifyActiveSession.pending, (state) => {
        state.verifySessionStatus = 'loading';
        state.verifySessionError = null;
      })
      .addCase(verifyActiveSession.fulfilled, (state, action) => {
        state.verifySessionStatus = 'succeeded';
        state.isSessionActive = action.payload.active;
      })
      .addCase(verifyActiveSession.rejected, (state, action) => {
        state.verifySessionStatus = 'failed';
        state.verifySessionError = action.payload || 'Failed to verify session';
      })

      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.token = null;
      })
      .addCase(logout.rejected, (state, action) => {
        state.signInError = action.payload as string;
      });
  },
});

// Export actions and reducer
export const { logOut } = authSlice.actions;
export default authSlice.reducer;
